import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config';
import { getValidToken } from '../../utils/tokenRefresh';
import './ChildScheduleView.css';

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  tier: string;
  programEnrollments?: any[];
}

interface ClassSession {
  id: string;
  date_available: string;
  start_time: string;
  end_time: string;
  program_id: string;
  program_name?: string;
  course_name?: string;
  location?: string;
  max_capacity?: number;
  enrolled_count?: number;
}

interface ScheduledSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  startTimeDisplay: string;
  endTimeDisplay: string;
  programName: string;
  programId: string;
  sessionId: string;
}

interface Program {
  id: string;
  name: string;
  programType: string;
  tier: string;
  color?: string;
}

interface ChildScheduleViewProps {
  child: Child;
  onClose: () => void;
  onSessionScheduled?: () => void;
}

const ChildScheduleView: React.FC<ChildScheduleViewProps> = ({ child, onClose, onSessionScheduled }) => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedProgramId, setSelectedProgramId] = useState<string>('all');
  const [availablePrograms, setAvailablePrograms] = useState<Program[]>([]);
  const [enrolledPrograms, setEnrolledPrograms] = useState<Program[]>([]);
  const [availableSessions, setAvailableSessions] = useState<ClassSession[]>([]);
  const [scheduledSessions, setScheduledSessions] = useState<ScheduledSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [programColors, setProgramColors] = useState<Map<string, string>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Color palette for programs
  const defaultColors = [
    '#3498db', // Blue
    '#e74c3c', // Red
    '#2ecc71', // Green
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#e67e22', // Dark Orange
    '#34495e', // Dark Blue
  ];

  // Helper function to format time for display
  const formatTimeDisplay = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return timeStr;
    }
  };

  // Get days in month helper
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Fetch enrolled programs for the child
  const fetchEnrolledPrograms = async () => {
    try {
      const token = await getValidToken();
      // Fetch the child's enrollments
      const response = await fetch(
        `${config.BACKEND_URL}/api/enrollments/student/${child.id}?status=active`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const enrollments = Array.isArray(data) ? data : (data.enrollments || []);
        
        // Extract unique programs from enrollments
        const programMap = new Map<string, Program>();
        enrollments.forEach((enrollment: any) => {
          if (enrollment.program_id && !programMap.has(enrollment.program_id)) {
            programMap.set(enrollment.program_id, {
              id: enrollment.program_id,
              name: enrollment.program_name || 'Unknown Program',
              programType: enrollment.program_type || 'unknown',
              tier: enrollment.program_tier || child.tier,
              color: enrollment.program_color || '#6c63ff'
            });
          }
        });
        
        const enrolledProgramsList = Array.from(programMap.values());
        setEnrolledPrograms(enrolledProgramsList);
        setAvailablePrograms(enrolledProgramsList); // Use enrolled programs as available programs
        
        // Set up color mapping
        const colorMap = new Map<string, string>();
        enrolledProgramsList.forEach((program, index) => {
          colorMap.set(program.id, program.color || defaultColors[index % defaultColors.length]);
        });
        setProgramColors(colorMap);
        
        // Set default selected program to "all" if there are enrolled programs
        if (enrolledProgramsList.length > 0 && selectedProgramId === 'all') {
          // Keep "all" as default
        }
      }
    } catch (error) {
      console.error('Error fetching enrolled programs:', error);
    }
  };

  // Fetch available sessions for the selected program (only for enrolled programs)
  const fetchAvailableSessions = async () => {
    // Only show sessions for programs the child is enrolled in
    if (enrolledPrograms.length === 0) {
      setAvailableSessions([]);
      return;
    }
    
    if (selectedProgramId === 'all') {
      // Fetch sessions for all enrolled programs
      const programIds = enrolledPrograms.map(p => p.id).join(',');
      if (!programIds) {
        setAvailableSessions([]);
        return;
      }
      try {
        const token = await getValidToken();
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        // Format dates as YYYY-MM-DD using local time (not UTC) to match calendar display
        const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const endDate = new Date(year, month + 1, 0);
        const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        
        const response = await fetch(
          `${config.BACKEND_URL}/api/class-sessions/?program_ids=${programIds}&from_date=${startDateStr}&to_date=${endDateStr}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and object response formats
          const sessions = Array.isArray(data) ? data : (data.sessions || data.data || []);
          // Filter to only include sessions for enrolled programs
          const enrolledProgramIds = new Set(enrolledPrograms.map(p => p.id));
          const filteredSessions = sessions.filter((s: ClassSession) => 
            s.program_id && enrolledProgramIds.has(s.program_id)
          );
          setAvailableSessions(filteredSessions);
        }
      } catch (error) {
        console.error('Error fetching available sessions:', error);
        setAvailableSessions([]);
      }
    } else {
      // Verify the selected program is in enrolled programs
      if (!enrolledPrograms.find(p => p.id === selectedProgramId)) {
        setAvailableSessions([]);
        return;
      }
      
      try {
        const token = await getValidToken();
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        // Format dates as YYYY-MM-DD using local time (not UTC) to match calendar display
        const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const endDate = new Date(year, month + 1, 0);
        const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        
        const response = await fetch(
          `${config.BACKEND_URL}/api/class-sessions/?program_id=${selectedProgramId}&from_date=${startDateStr}&to_date=${endDateStr}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and object response formats
          const sessions = Array.isArray(data) ? data : (data.sessions || data.data || []);
          setAvailableSessions(sessions);
        }
      } catch (error) {
        console.error('Error fetching available sessions:', error);
        setAvailableSessions([]);
      }
    }
  };

  // Fetch scheduled sessions for the child
  const fetchScheduledSessions = async () => {
    try {
      const token = await getValidToken();
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      // Format dates as YYYY-MM-DD using local time (not UTC) to match calendar display
      const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endDate = new Date(year, month + 1, 0);
      const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/students/${child.id}/schedule?start_date=${startDateStr}&end_date=${endDateStr}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const mappedSessions = (data.schedule || []).map((item: any) => {
          // Extract date part from schedule_date (handle both YYYY-MM-DD and ISO datetime formats)
          let scheduleDate = item.schedule_date;
          if (scheduleDate) {
            if (typeof scheduleDate === 'string') {
              // If it's an ISO datetime string, extract just the date part
              scheduleDate = scheduleDate.split('T')[0];
            } else if (scheduleDate instanceof Date) {
              // If it's a Date object, format as YYYY-MM-DD using local time
              const year = scheduleDate.getFullYear();
              const month = scheduleDate.getMonth();
              const day = scheduleDate.getDate();
              scheduleDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            }
          }
          
          // Extract time part from start_time and end_time (handle ISO datetime format)
          let startTime = item.start_time;
          let endTime = item.end_time;
          if (startTime && typeof startTime === 'string' && startTime.includes('T')) {
            // If it's an ISO datetime, extract just the time part
            const timeMatch = startTime.match(/T(\d{2}:\d{2}:\d{2})/);
            if (timeMatch) {
              startTime = timeMatch[1].substring(0, 5); // Extract HH:MM
            }
          }
          if (endTime && typeof endTime === 'string' && endTime.includes('T')) {
            // If it's an ISO datetime, extract just the time part
            const timeMatch = endTime.match(/T(\d{2}:\d{2}:\d{2})/);
            if (timeMatch) {
              endTime = timeMatch[1].substring(0, 5); // Extract HH:MM
            }
          }
          
          return {
            id: item.session_id,
            date: scheduleDate,
            startTime: startTime,
            endTime: endTime,
            startTimeDisplay: formatTimeDisplay(startTime),
            endTimeDisplay: formatTimeDisplay(endTime),
            programName: item.program_name,
            programId: item.program_id,
            sessionId: item.session_id
          };
        });
        setScheduledSessions(mappedSessions);
      }
    } catch (error) {
      console.error('Error fetching scheduled sessions:', error);
      setScheduledSessions([]);
    }
  };

  // Schedule a session
  const handleScheduleSession = async (sessionId: string, programId: string) => {
    try {
      setLoading(true);
      const token = await getValidToken();
      
      const response = await fetch(`${config.BACKEND_URL}/api/course-registrations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: child.id,
          programId: programId,
          selectedSessions: [sessionId]
        })
      });

      if (response.ok) {
        await fetchScheduledSessions();
        await fetchAvailableSessions();
      } else {
        const error = await response.json();
        alert(`Failed to schedule session: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error scheduling session:', error);
      alert('Failed to schedule session');
    } finally {
      setLoading(false);
    }
  };

  // Unschedule a session
  const handleUnscheduleSession = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to remove this session?')) {
      return;
    }

    try {
      setLoading(true);
      const token = await getValidToken();
      
      const response = await fetch(
        `${config.BACKEND_URL}/api/course-registrations?student_id=${child.id}&session_id=${sessionId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        await fetchScheduledSessions();
        await fetchAvailableSessions();
        // Notify parent component to refresh upcoming sessions
        if (onSessionScheduled) {
          onSessionScheduled();
        }
      } else {
        const error = await response.json();
        alert(`Failed to remove session: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error removing session:', error);
      alert('Failed to remove session');
    } finally {
      setLoading(false);
    }
  };

  // Handle day click - on mobile, just select the date to show in detail panel
  const handleDayClick = (date: Date) => {
    // Format date as YYYY-MM-DD using local time (not UTC) to avoid timezone shifts
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
    
    if (isPast) {
      return; // Don't allow selection of past dates
    }

    // Check if there are any sessions (scheduled or available) for this date
    const scheduled = getSessionForDate(dateString);
    const available = getAvailableSessionsForDate(dateString);
    
    if (scheduled || available.length > 0) {
      setSelectedDate(dateString);
    }
  };

  // Handle session toggle from detail panel
  const handleSessionToggle = async (sessionId: string, programId: string, isScheduled: boolean) => {
    if (isScheduled) {
      await handleUnscheduleSession(sessionId);
    } else {
      await handleScheduleSession(sessionId, programId);
    }
    // Keep the date selected to show updated state
  };

  // Get session for a specific date
  const getSessionForDate = (dateString: string): ScheduledSession | undefined => {
    return scheduledSessions.find(s => {
      // Normalize both dates to YYYY-MM-DD format for comparison
      let sessionDate = s.date;
      if (sessionDate && typeof sessionDate === 'string') {
        sessionDate = sessionDate.split('T')[0];
      }
      return sessionDate === dateString;
    });
  };

  // Get available sessions for a specific date
  const getAvailableSessionsForDate = (dateString: string): ClassSession[] => {
    return availableSessions.filter(s => {
      // Handle different date field names and formats
      let sessionDate = s.date_available;
      if (sessionDate && typeof sessionDate === 'string') {
        // Extract date part if it's a datetime string
        sessionDate = sessionDate.split('T')[0];
      }
      return sessionDate === dateString;
    });
  };

  // Get program color
  const getProgramColor = (programId: string): string => {
    return programColors.get(programId) || '#3498db';
  };

  useEffect(() => {
    fetchEnrolledPrograms();
  }, [child]);

  useEffect(() => {
    if (enrolledPrograms.length > 0) {
      fetchAvailableSessions();
      fetchScheduledSessions();
    }
  }, [selectedProgramId, currentMonth, enrolledPrograms]);

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content child-schedule-modal large" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header schedule-header-modern" style={{ 
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          padding: '12px 16px',
          minHeight: '48px',
          height: 'auto',
          borderBottom: '1px solid #f3f4f6',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
            Schedule - {child.firstName} {child.lastName}
          </h2>
          <button 
            className="close-button-modern" 
            onClick={onClose}
            style={{
              position: 'relative',
              zIndex: 51,
              flexShrink: 0,
              marginLeft: '10px',
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              fontSize: '1.5rem',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color 0.2s ease',
              padding: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#4b5563'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
          >×</button>
        </div>

        <div className="modal-body schedule-modal-body" style={{ 
          flex: 1,
          overflowY: 'auto',
          minHeight: 0
        }}>
          {/* Program Selection - Desktop Only */}
          <div className="form-group desktop-only" style={{ marginBottom: '20px' }}>
            <label htmlFor="program-select" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Program <span className="required">*</span>
            </label>
            <select
              id="program-select"
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="form-control"
              style={{ width: '100%', padding: '10px', fontSize: '16px' }}
            >
              <option value="all">All Enrolled Programs</option>
              {enrolledPrograms.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name} {program.programType && program.programType !== 'unknown' ? `(${program.programType.replace('_', ' ')})` : ''}
                </option>
              ))}
            </select>
            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
              Select a program to filter available sessions, or "All Enrolled Programs" to see all sessions for enrolled programs
            </small>
            {enrolledPrograms.length === 0 && (
              <div style={{ 
                marginTop: '10px', 
                padding: '10px', 
                background: '#fff3cd', 
                border: '1px solid #ffc107',
                borderRadius: '4px',
                color: '#856404'
              }}>
                ⚠️ No active program enrollments found. Please enroll in a program first.
              </div>
            )}
          </div>

          {/* Mobile Program Filter Chips */}
          <div className="program-filter-chips mobile-only" style={{ 
            marginBottom: '8px',
            padding: '8px 16px'
          }}>
            <div style={{ 
              display: 'flex',
              overflowX: 'auto',
              gap: '8px',
              paddingBottom: '4px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }} className="filter-chips-scroll">
              <button
                onClick={() => setSelectedProgramId('all')}
                style={{
                  flexShrink: 0,
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  borderRadius: '9999px',
                  border: '1px solid #e5e7eb',
                  background: selectedProgramId === 'all' ? '#9333ea' : '#f3f4f6',
                  color: selectedProgramId === 'all' ? 'white' : '#4b5563',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                All Programs
              </button>
              {enrolledPrograms.map((program) => (
                <button
                  key={program.id}
                  onClick={() => setSelectedProgramId(program.id)}
                  style={{
                    flexShrink: 0,
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '9999px',
                    border: '1px solid #e5e7eb',
                    background: selectedProgramId === program.id ? '#9333ea' : '#f3f4f6',
                    color: selectedProgramId === program.id ? 'white' : '#4b5563',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {program.name}
                </button>
              ))}
            </div>
          </div>

          {/* Instructions - Desktop Only */}
          <div className="desktop-only" style={{ 
            background: '#f8f9fa', 
            padding: '15px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #e9ecef'
          }}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              💡 <strong>How to schedule:</strong> Click on a calendar day to schedule or unschedule sessions.
              <br />
              <small>
                🟢 Green = Scheduled session (click to unschedule) | 🔵 Blue = Available session (click to schedule) | 
                ⚪ Gray = No sessions available
              </small>
            </p>
          </div>

          {/* Calendar Container - Split View for Mobile */}
          <div className="schedule-split-view">
            {/* Calendar Section - Top Half on Mobile */}
            <div className="calendar-section">
              <div className="calendar-container" style={{ width: '100%' }}>
                <div className="calendar-header">
                  <button
                    className="calendar-nav-button"
                    onClick={() => {
                      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
                      setSelectedDate(null);
                    }}
                    aria-label="Previous month"
                  >
                    &lt;
                  </button>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                    <h3 className="calendar-month-title">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    {/* Compact Legend - Inline with month */}
                    <div className="mobile-only" style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '9px',
                      color: '#6b7280'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4caf50' }}></div>
                        <span>Scheduled</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', border: '1.5px solid #2196f3', backgroundColor: 'transparent' }}></div>
                        <span>Available</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="calendar-nav-button"
                    onClick={() => {
                      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
                      setSelectedDate(null);
                    }}
                    aria-label="Next month"
                  >
                    &gt;
                  </button>
                </div>

            <div className="calendar-grid-unified">
              {/* Day names row */}
              {dayNames.map(name => (
                <div key={name} className="calendar-day-name">
                  {name}
                </div>
              ))}
              
              {/* Empty cells for days before month starts */}
              {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="calendar-day empty"></div>
              ))}

              {/* Days of the month */}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const date = new Date(year, month, day);
                // Format date as YYYY-MM-DD using local time (not UTC) to avoid timezone shifts
                const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const scheduled = getSessionForDate(dateString);
                const available = getAvailableSessionsForDate(dateString);
                const isToday = new Date().toDateString() === date.toDateString();
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                const isSelected = selectedDate === dateString;
                const totalSessions = scheduled ? 1 : available.length;

                // Determine visual style for mobile (borderless)
                let textColor = '#333';
                let cursor = 'default';
                let showGreenCircle = false;
                let showBlueText = false;

                if (scheduled) {
                  // Scheduled session - Green circle background
                  showGreenCircle = true;
                  textColor = '#ffffff';
                  cursor = 'pointer';
                } else if (available.length > 0 && !isPast) {
                  // Available session but not scheduled - Blue text
                  showBlueText = true;
                  textColor = '#2196f3';
                  cursor = 'pointer';
                } else if (isPast) {
                  // Past date - Grey text
                  textColor = '#999';
                } else {
                  // No sessions available - Grey text
                  textColor = '#9e9e9e';
                }

                return (
                  <div
                    key={day}
                    className={`calendar-day ${isSelected ? 'selected' : ''} ${showGreenCircle ? 'scheduled' : ''} ${showBlueText ? 'available' : ''}`}
                    onClick={() => !isPast && (scheduled || available.length > 0) ? handleDayClick(date) : undefined}
                    style={{
                      cursor: cursor,
                      position: 'relative',
                      transition: 'all 0.2s'
                    }}
                  >
                    {/* Desktop: Show full details */}
                    <div className="desktop-only" style={{ width: '100%' }}>
                      <div className="calendar-day-number" style={{ 
                        fontWeight: isToday ? 'bold' : 'normal',
                        fontSize: '14px',
                        marginBottom: '4px'
                      }}>
                        {day}
                      </div>
                      {scheduled && (
                        <div className="calendar-session-details" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                          <div style={{ fontWeight: '600', marginBottom: '1px', fontSize: '9px' }}>
                            {scheduled.programName.length > 12 ? scheduled.programName.substring(0, 10) + '...' : scheduled.programName}
                          </div>
                          <div style={{ fontSize: '8px' }}>
                            {scheduled.startTimeDisplay} - {scheduled.endTimeDisplay}
                          </div>
                        </div>
                      )}
                      {!scheduled && available.length > 0 && !isPast && (
                        <div className="calendar-available-sessions" style={{ fontSize: '9px', lineHeight: '1.2' }}>
                          <div style={{ fontWeight: '600', marginBottom: '1px', fontSize: '8px' }}>
                            {available.length} avail
                          </div>
                          {available.slice(0, 1).map((session, idx) => (
                            <div key={idx} style={{ marginTop: '1px', fontSize: '7px' }}>
                              {formatTimeDisplay(session.start_time)}-{formatTimeDisplay(session.end_time)}
                            </div>
                          ))}
                          {available.length > 1 && (
                            <div style={{ marginTop: '1px', fontStyle: 'italic', fontSize: '7px' }}>
                              +{available.length - 1}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Mobile: Show date number with visual indicators */}
                    {showGreenCircle ? (
                      <div className="mobile-only scheduled-circle">
                        <div className="calendar-day-number scheduled-number">
                          {day}
                        </div>
                      </div>
                    ) : showBlueText ? (
                      <div className="mobile-only available-circle">
                        <div className="calendar-day-number available-number">
                          {day}
                        </div>
                      </div>
                    ) : (
                      <div className="mobile-only">
                        <div className="calendar-day-number" style={{ 
                          fontWeight: isToday ? 'bold' : 'normal',
                          color: textColor,
                          lineHeight: '1'
                        }}>
                          {day}
                        </div>
                        {/* Dots for multiple sessions */}
                        {totalSessions > 1 && (
                          <div style={{ 
                            display: 'flex', 
                            gap: '3px', 
                            marginTop: '4px',
                            justifyContent: 'center'
                          }}>
                            {Array.from({ length: Math.min(totalSessions, 3) }).map((_, idx) => (
                              <div key={idx} style={{
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                backgroundColor: '#2196f3'
                              }}></div>
                            ))}
                            {totalSessions > 3 && (
                              <span style={{ fontSize: '8px', marginLeft: '2px' }}>+</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Today indicator - small dot underneath */}
                    {isToday && (
                      <div className="today-indicator"></div>
                    )}
                  </div>
                );
              })}
            </div>
            </div>
          </div>

          {/* Detail Panel - Mobile Only (Fixed Bottom Sheet) */}
          <div className="schedule-detail-panel mobile-only schedule-bottom-sheet">
            {selectedDate ? (
              <div className="detail-panel-content">
                <div className="detail-panel-header">
                  <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                    {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </h4>
                  <button
                    onClick={() => setSelectedDate(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '20px',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '0',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Scheduled Session */}
                {(() => {
                  const scheduled = getSessionForDate(selectedDate);
                  if (scheduled) {
                    return (
                      <div className="session-detail-card scheduled">
                        <div className="session-detail-header">
                          <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {scheduled.programName}
                          </h5>
                          <label className="session-toggle">
                            <input
                              type="checkbox"
                              checked={true}
                              onChange={() => handleSessionToggle(scheduled.sessionId, scheduled.programId, true)}
                              disabled={loading}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        <div className="session-detail-info">
                          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                            <strong>Time:</strong> {scheduled.startTimeDisplay} - {scheduled.endTimeDisplay}
                          </div>
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            Tap toggle to unschedule
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Available Sessions */}
                {(() => {
                  const available = getAvailableSessionsForDate(selectedDate);
                  const scheduled = getSessionForDate(selectedDate);
                  
                  if (available.length > 0 && !scheduled) {
                    return (
                      <div className="available-sessions-list">
                        {available.map((session) => {
                          const isScheduled = scheduledSessions.some(s => {
                            // Normalize date for comparison
                            let sessionDate = s.date;
                            if (sessionDate && typeof sessionDate === 'string') {
                              sessionDate = sessionDate.split('T')[0];
                            }
                            return sessionDate === selectedDate && s.sessionId === session.id;
                          });
                          return (
                            <div key={session.id} className="session-detail-card available">
                              <div className="session-detail-header">
                                <div>
                                  <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                    {session.program_name || 'Program'}
                                  </h5>
                                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                                    {formatTimeDisplay(session.start_time)} - {formatTimeDisplay(session.end_time)}
                                  </div>
                                </div>
                                <label className="session-toggle">
                                  <input
                                    type="checkbox"
                                    checked={isScheduled}
                                    onChange={() => handleSessionToggle(session.id, session.program_id, isScheduled)}
                                    disabled={loading}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}

                {!getSessionForDate(selectedDate) && getAvailableSessionsForDate(selectedDate).length === 0 && (
                  <div style={{ 
                    padding: '20px', 
                    textAlign: 'center', 
                    color: '#999',
                    fontSize: '14px'
                  }}>
                    No sessions available for this date
                  </div>
                )}

                {/* Done Button */}
                <button
                  onClick={onClose}
                  style={{
                    position: 'absolute',
                    bottom: `calc(env(safe-area-inset-bottom, 20px) + 16px)`,
                    left: '16px',
                    right: '16px',
                    width: 'calc(100% - 32px)',
                    padding: '14px 20px',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.2s ease',
                    zIndex: 51
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="detail-panel-placeholder" style={{ position: 'relative', paddingBottom: '80px' }}>
                <p style={{ margin: 0, color: '#999', fontSize: '14px', textAlign: 'center' }}>
                  Tap a date to view session details
                </p>
                {/* Done Button - Always visible */}
                <button
                  onClick={onClose}
                  style={{
                    position: 'absolute',
                    bottom: `calc(env(safe-area-inset-bottom, 20px) + 16px)`,
                    left: '16px',
                    right: '16px',
                    width: 'calc(100% - 32px)',
                    padding: '14px 20px',
                    backgroundColor: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.2s ease',
                    zIndex: 51
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
                >
                  Done
                </button>
              </div>
            )}
          </div>
          </div>

        </div>

        <div className="modal-footer" style={{ 
          padding: '15px 20px', 
          borderTop: '1px solid #e9ecef',
          background: 'white',
          flexShrink: 0,
          marginTop: 'auto'
        }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChildScheduleView;

